import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useHub, usePrivate } from '../context';
import { isArmor, isCrafted, isLocked, isMasterwork, isWeapon, loadRolled, type Rolled, type RolledPlug, type RolledStat, type StashItem } from '../character';
import type { HubChar } from '../hub';
import { armourTotal, canWear, keyOf, TAGS, type Spot, type Tag, type Where } from '../items';
import { Pic } from '../ui';
import { useTable } from '../../lib/manifest';
import type { ItemDef } from '../../lib/tables';
import type { GodMark, RollGroup } from '../godrolls';
import { GodThumb } from './ItemViews';
import type { SavedLoadout } from './ItemManager';

/**
 * The card an item opens, in DIM's shape: the item's banner, Overview (notes, its counts, stats as coloured bars, frame,
 * perk columns with their tooltips, mods) and Triage (god rolls, its copies), and beside it the actions (tag, lock,
 * compare, loadout, infuse, vault, equip on, pull to).
 */

export interface InspectProps {
  spot: Spot; chars: HubChar[]; narrow: boolean; at: { x: number; y: number };
  rolled?: Rolled;   // the roll, built from the read of every item's roll (else the card reads its own)
  busy: boolean; tag?: Tag; setTag: (t?: Tag) => void; note: string; setNote: (v: string) => void;
  god?: GodMark; groups: RollGroup[]; elementIcons?: Record<number, string>;
  copies: Spot[]; fuel: Spot[]; loadouts: SavedLoadout[]; copyTag: (id: string) => Tag | undefined;
  onLock: () => void; go: (to: Where, wear: boolean) => void; compare: () => void; open: (id: string) => void;
  infuse: (fuel: Spot) => void; addToLoadout: (l: SavedLoadout) => void; info: () => void;
}

const CARD_W = 330, PANEL_W = 168;
const SIDE_KEY = 'd2c.im.panel';

export function ItemInspect(p: InspectProps) {
  const i = p.spot.item, id = keyOf(i);
  const [tab, setTab] = useState<'overview' | 'triage'>('overview');
  const [panel, setPanel] = useState(() => { try { return localStorage.getItem(SIDE_KEY) !== 'shut'; } catch { return true; } });
  const togglePanel = () => setPanel((v) => { try { localStorage.setItem(SIDE_KEY, v ? 'shut' : 'open'); } catch { /* storage blocked */ } return !v; });
  const box = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(Math.max(8, p.at.y - 20));
  // DIM puts the actions on the side with room; the card moves up until it fits the window
  const width = CARD_W + (panel ? PANEL_W : 34);
  const right = p.at.x + 12 + width <= window.innerWidth - 8;
  const left = right ? p.at.x + 12 : Math.max(8, p.at.x - 12 - width);
  useLayoutEffect(() => {
    const h = box.current?.offsetHeight ?? 0;
    setTop(Math.max(8, Math.min(p.at.y - 20, window.innerHeight - h - 8)));
  }, [p.at.y, tab, panel, id]);
  // the card stays with its item as the page (or the board) scrolls, as DIM's does: it moves by however far the item moved
  const [moved, setMoved] = useState({ x: 0, y: 0 });
  useLayoutEffect(() => {
    if (p.narrow) return;
    const square = () => document.querySelector(`[data-item="${CSS.escape(id)}"]`)?.getBoundingClientRect();
    const start = square();
    if (!start) return;
    let frame = 0;
    const follow = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { const r = square(); if (r) setMoved({ x: r.left - start.left, y: r.top - start.top }); });
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('scroll', follow, true); window.removeEventListener('resize', follow); };
  }, [id, p.narrow]);
  const where = p.spot.owner === null ? 'In the vault' : `${p.spot.worn ? 'Worn' : 'Carried'} by your ${p.chars.find((c) => c.id === p.spot.owner)?.cls ?? 'character'}`;

  const card = (
    <div className={`im-card t${i.tier}`}>
      <div className="im-card-head">
        {i.watermark && <Pic src={i.watermark} className="im-card-wm" />}
        <div className="im-card-words">
          <b>{i.name}</b>
          <small>{i.typeName}{isWeapon(i) && i.ammo > 0 && <Ammo kind={i.ammo} />}{isCrafted(i) && <span className="im-card-chip">Shaped</span>}</small>
        </div>
        <span className="im-card-power">
          {p.god && <GodThumb mark={p.god} />}
          {isWeapon(i) && p.elementIcons?.[i.damage] ? <Pic src={p.elementIcons[i.damage]} className="im-elicon" /> : i.power !== undefined && <i className={`im-el e${isWeapon(i) ? i.damage : 0}`} />}
          {i.power}
        </span>
      </div>
      <div className="im-card-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'overview'} className={tab === 'overview' ? 'on' : ''} onClick={() => setTab('overview')}>Overview</button>
        <button type="button" role="tab" aria-selected={tab === 'triage'} className={tab === 'triage' ? 'on' : ''} onClick={() => setTab('triage')}>
          Triage{p.god && <span className="im-card-thumb" title="A community god roll">👍</span>}
        </button>
      </div>
      {tab === 'overview' ? (
        <>
          <NoteRow key={id} value={p.note} save={p.setNote} />
          <div className="im-card-where"><span>{where}</span>{isMasterwork(i) && <span className="gold">Masterwork</span>}</div>
          {(isWeapon(i) || isArmor(i)) && i.instanceId ? <Roll key={i.instanceId} item={i} groups={p.groups} ready={p.rolled} /> : <div className="im-roll wait">Nothing more to show for this item.</div>}
        </>
      ) : (
        <Triage {...p} />
      )}
    </div>
  );

  const actions = (
    <div className={`im-actions ${panel ? '' : 'shut'}`}>
      {!p.narrow && (
        <button type="button" className="im-act-fold" onClick={togglePanel} title={panel ? 'Fold the actions away' : 'Show the actions'} aria-label={panel ? 'Fold the actions away' : 'Show the actions'}>
          {(panel === right) ? '«' : '»'}
        </button>
      )}
      {(panel || p.narrow) && <Actions {...p} />}
    </div>
  );

  const node = (
    <div ref={box} className={`im-inspect ${p.narrow ? 'sheet' : right ? 'right' : 'left'}`} role="dialog" aria-label={i.name} onClick={(e) => e.stopPropagation()}
      style={p.narrow ? undefined : { left: left + moved.x, top: top + moved.y }}>
      {p.narrow || right ? <>{card}{actions}</> : <>{actions}{card}</>}
    </div>
  );
  return createPortal(node, document.body);
}

// ---------------- the actions beside the card ----------------

function Actions(p: InspectProps) {
  const i = p.spot.item;
  const [sub, setSub] = useState<'loadout' | 'infuse'>();
  const [said, setSaid] = useState<string>();
  const vaultIcon = <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3.5 4h17v16h-17zM8 12a4 4 0 1 0 8 0a4 4 0 1 0-8 0" /></svg>;
  return (
    <div className="im-act-list">
      <label className="im-act-tag">
        <span className={`dot ${p.tag ? `t-${p.tag}` : ''}`}>{p.tag ? TAGS.find(([t]) => t === p.tag)?.[2] : '●'}</span>
        <select value={p.tag ?? ''} onChange={(e) => p.setTag((e.target.value || undefined) as Tag | undefined)} aria-label="Tag item">
          <option value="">Tag Item</option>
          {TAGS.map(([t, label]) => <option key={t} value={t}>{label}</option>)}
        </select>
      </label>
      <button type="button" className={`im-act ${isLocked(i) ? 'on' : ''}`} disabled={p.busy} onClick={p.onLock} title={isLocked(i) ? 'Unlock it in the game (L)' : 'Lock it in the game, so it cannot be dismantled (L)'}>
        <LockIcon locked={isLocked(i)} />{isLocked(i) ? 'Locked' : 'Unlocked'}
      </button>
      {(isWeapon(i) || isArmor(i)) && <button type="button" className="im-act" onClick={p.compare} title={`Compare with its copies and every ${i.typeName}`}><CompareIcon />Compare</button>}
      {(isWeapon(i) || isArmor(i)) && (
        <button type="button" className={`im-act ${sub === 'loadout' ? 'open' : ''}`} onClick={() => setSub(sub === 'loadout' ? undefined : 'loadout')} aria-expanded={sub === 'loadout'}><PlusIcon />Loadout</button>
      )}
      {sub === 'loadout' && (
        <div className="im-act-sub">
          {p.loadouts.length === 0 && <span className="im-act-hint">No saved loadouts for this class yet. Save one from a character's ⋮ menu.</span>}
          {p.loadouts.map((l) => {
            const has = l.items.some((x) => x.instanceId === i.instanceId);
            return (
              <button key={l.id} type="button" disabled={has} title={has ? 'Already in this loadout' : `Put it in ${l.name}, in place of what that loadout has in this slot`}
                onClick={() => { p.addToLoadout(l); setSaid(`Added to ${l.name}.`); setSub(undefined); }}>
                {l.name}{has && <small>has it</small>}
              </button>
            );
          })}
        </div>
      )}
      {i.power !== undefined && (isWeapon(i) || isArmor(i)) && (
        <button type="button" className={`im-act ${sub === 'infuse' ? 'open' : ''}`} onClick={() => setSub(sub === 'infuse' ? undefined : 'infuse')} aria-expanded={sub === 'infuse'} title="Find something with more Power to infuse into it">
          <InfuseIcon />Infuse
        </button>
      )}
      {sub === 'infuse' && (
        <div className="im-act-sub">
          {p.fuel.length === 0 && <span className="im-act-hint">Nothing you hold for this slot has more Power.</span>}
          {p.fuel.map((f) => (
            <button key={keyOf(f.item)} type="button" className="fuel" title={`Bring ${f.item.name} and this item to one character, ready to infuse in the game`} onClick={() => p.infuse(f)}>
              <Pic src={f.item.icon} width={24} height={24} /><span>{f.item.name}<small>{f.owner === null ? 'Vault' : p.chars.find((c) => c.id === f.owner)?.cls}{p.copyTag(keyOf(f.item)) === 'junk' ? ' · junk' : ''}</small></span><b>{f.item.power}</b>
            </button>
          ))}
        </div>
      )}
      {p.spot.owner !== null && <button type="button" className="im-act" onClick={() => p.go(null, false)} title="Put it in the vault (V)"><span className="im-act-ic">{vaultIcon}</span>Vault</button>}
      {said && <span className="im-act-said">{said}</span>}
      <span className="im-act-label">Equip on:</span>
      <div className="im-act-chars">
        {p.chars.map((c, k) => {
          const here = p.spot.owner === c.id && p.spot.worn;
          return (
            <button key={c.id} type="button" className={here ? 'here' : ''} disabled={here || !canWear(i, c)} onClick={() => p.go(c.id, true)}
              title={here ? `Worn by your ${c.cls}` : canWear(i, c) ? `Equip on your ${c.cls} (${k + 1})` : `Your ${c.cls} cannot wear this`}>
              <Pic src={c.emblem} width={34} height={34} />
            </button>
          );
        })}
      </div>
      <span className="im-act-label">Pull to:</span>
      <div className="im-act-chars">
        {p.chars.map((c) => {
          const here = p.spot.owner === c.id && !p.spot.worn;
          return (
            <button key={c.id} type="button" className={here ? 'here' : ''} disabled={here} onClick={() => p.go(c.id, false)} title={here ? `Carried by your ${c.cls}` : `Move to your ${c.cls}`}>
              <Pic src={c.emblem} width={34} height={34} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Triage: god rolls and the copies held ----------------

function Triage(p: InspectProps) {
  const i = p.spot.item;
  const seen = new Set<string>();
  const why = (p.god?.groups ?? []).filter((g) => { const k = `${g.mode}|${g.note}`; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 4);
  const better = p.copies.filter((c) => (c.item.power ?? 0) > (i.power ?? 0)).length;
  return (
    <div className="im-triage">
      <section>
        <h4>Wish list</h4>
        {p.god ? (
          <>
            <div className="im-card-god-head"><GodThumb mark={p.god} /><b>God roll</b>{p.god.pve && <span className="pve">PvE</span>}{p.god.pvp && <span className="pvp">PvP</span>}</div>
            {why.map((g, k) => <p key={k}><span className={g.mode}>{g.mode === 'any' ? 'Both' : g.mode.toUpperCase()}</span>{g.note || 'On the community wish list.'}</p>)}
          </>
        ) : <p className="im-dim">{p.groups.length ? 'This roll does not match the community wish list.' : 'The community wish list has nothing for this item.'}</p>}
      </section>
      <section>
        <h4>Copies held <b>{p.copies.length + 1}</b></h4>
        {p.copies.length === 0 ? <p className="im-dim">This is your only copy.</p> : (
          <>
            <p className="im-dim">{better ? `${better} of your other copies ${better === 1 ? 'has' : 'have'} more Power.` : 'This copy has the most Power.'}</p>
            <div className="im-copies">
              {p.copies.slice(0, 12).map((c) => (
                <button key={keyOf(c.item)} type="button" onClick={() => p.open(keyOf(c.item))} title={`${c.item.name}\n${c.owner === null ? 'Vault' : p.chars.find((x) => x.id === c.owner)?.cls ?? ''}`}>
                  <Pic src={c.item.icon} width={36} height={36} />
                  <span>{c.item.power ?? ''}</span>
                  {p.copyTag(keyOf(c.item)) && <i className={`t-${p.copyTag(keyOf(c.item))}`}>{TAGS.find(([t]) => t === p.copyTag(keyOf(c.item)))?.[2]}</i>}
                </button>
              ))}
            </div>
          </>
        )}
      </section>
      <button type="button" className="im-card-link" onClick={p.info}>Perks, stats and god rolls in D2 Info ›</button>
      <div className="im-card-keys">Keys: <b>L</b> lock  <b>V</b> vault  <b>1</b>-<b>{p.chars.length}</b> equip  <b>J</b> junk  <b>K</b> keep  <b>Esc</b> close</div>
    </div>
  );
}

// ---------------- Overview: the roll ----------------

/** What this one copy rolled: its counts, stats as DIM's coloured bars, frame, perk columns (tooltips on hover) and mods. */
function Roll({ item, groups, ready }: { item: StashItem; groups: RollGroup[]; ready?: Rolled }) {
  const h = useHub();
  const { data: own, error } = usePrivate<Rolled>(ready ? undefined : `rolled:${item.instanceId}`, (t) => loadRolled(h.player!, t, item));
  const data = ready ?? own;
  const lite = useTable<ItemDef>('DestinyInventoryItemLiteDefinition');
  const [tip, setTip] = useState<{ plug: RolledPlug; rect: DOMRect }>();
  const [asList, setAsList] = useState(false);
  const [wish, setWish] = useState(false);
  // the perks the community's rolls for this weapon ask for, PvE and PvP apart (by name: an enhanced perk has its own hash)
  const liked = new Map<string, Set<string>>();
  for (const g of groups) for (const c of g.cols) for (const x of c) { const n = lite?.[x]?.name; if (n) (liked.get(n) ?? liked.set(n, new Set()).get(n)!).add(g.mode); }
  const likedOf = (n: string) => liked.get(n) ?? liked.get(n.replace(/^Enhanced /, ''));
  if (!data) return <div className="im-roll wait">{error ?? 'Reading this roll from Bungie...'}</div>;

  const show = (plug: RolledPlug) => (e: { currentTarget: Element }) => setTip({ plug, rect: e.currentTarget.getBoundingClientRect() });
  const plugEl = (x: RolledPlug, kind: string, sel = true) => {
    const modes = likedOf(x.name);
    return (
      <span key={`${kind}${x.hash}`} className={`im-plug ${kind} ${modes ? 'liked' : ''} ${x.enhanced ? 'enh' : ''}`} tabIndex={0}
        onMouseEnter={show(x)} onMouseLeave={() => setTip(undefined)} onFocus={show(x)} onBlur={() => setTip(undefined)}
        onClick={(e) => { e.stopPropagation(); setTip(tip?.plug.hash === x.hash ? undefined : { plug: x, rect: e.currentTarget.getBoundingClientRect() }); }}>
        <Pic src={x.icon} />
        {x.enhanced && <i className="im-enh" aria-label="Enhanced">▲</i>}
        {modes && (sel || wish) && <i className="im-wish" aria-label="On the wish list">👍</i>}
      </span>
    );
  };
  const trackers = data.objectives.filter((o) => o.tracker);
  const goals = data.objectives.filter((o) => !o.tracker);
  const intrinsic = data.intrinsic;
  const rpm = data.stats.find((s) => /Rounds Per Minute|Draw Time|Charge Time/.test(s.name));
  const impact = data.stats.find((s) => s.name === 'Impact');
  return (
    <div className="im-roll">
      {goals.length > 0 && (
        <section className="im-goals">
          <h4><CatalystIcon />{isWeapon(item) && item.tier === 6 ? 'Catalyst Progress' : 'Objectives'}</h4>
          {goals.map((o, k) => (
            <div key={k} className={`im-goal ${o.done ? 'done' : ''}`}>
              <i className="box">{o.done ? '✓' : ''}</i>
              <span>{o.name}</span>
              <b>{o.total > 1 ? `${o.progress.toLocaleString()}/${o.total.toLocaleString()}` : o.done ? 'Done' : ''}</b>
              {o.total > 1 && <em style={{ width: `${Math.min(100, (o.progress / o.total) * 100)}%` }} />}
            </div>
          ))}
        </section>
      )}
      {trackers.map((o, k) => <div key={k} className="im-tracker"><SkullIcon /><span>{o.name}</span><b>{o.progress.toLocaleString()}</b></div>)}
      {data.stats.length > 0 && (
        <div className="im-stats">
          {isArmor(item) && <div className="im-stat total"><span>Total</span><b>{armourTotal(data)}</b><i className="none" /></div>}
          {data.stats.map((s) => <StatRow key={s.name} s={s} />)}
        </div>
      )}
      {intrinsic && (
        <div className="im-intrinsic" onMouseEnter={show(intrinsic)} onMouseLeave={() => setTip(undefined)}>
          {plugEl(intrinsic, 'frame')}
          <span><b>{intrinsic.name}</b><small>{rpm && impact ? `${rpm.value} ${rpm.name === 'Rounds Per Minute' ? 'rpm' : 'ms'} / ${impact.value} impact` : intrinsic.type}</small></span>
        </div>
      )}
      {data.columns.length > 0 ? (
        <div className="im-perkarea">
          {asList ? (
            <div className="im-perklist">
              {data.columns.map((c, k) => {
                const x = c.options.find((o) => o.hash === c.selected) ?? c.options[0];
                return (
                  <div key={k} className="im-perkline">
                    {plugEl(x, 'perk')}
                    <span><b className={likedOf(x.name) ? 'liked' : ''}>{x.name}</b><small>{x.desc}</small></span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`im-cols ${wish ? 'wish' : ''}`}>
              {data.columns.map((c, k) => (
                <div key={k} className="im-col">{c.options.map((x) => <span key={x.hash} className={x.hash === c.selected ? 'sel' : 'alt'}>{plugEl(x, 'perk', x.hash === c.selected)}</span>)}</div>
              ))}
            </div>
          )}
          <button type="button" className={`im-perkview ${asList ? 'on' : ''}`} onClick={() => setAsList(!asList)} title={asList ? 'Show the perk columns' : 'Show the perks as a list, with what they do'} aria-pressed={asList}>
            <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor"><path d="M2 3h2v2H2zM6 3.5h8v1H6zM2 7h2v2H2zM6 7.5h8v1H6zM2 11h2v2H2zM6 11.5h8v1H6z" /></svg>
          </button>
        </div>
      ) : data.perks.length > 0 && <div className="im-plugs">{data.perks.map((x) => plugEl(x, 'perk'))}</div>}
      {data.mods.length > 0 && <div className="im-plugs mods">{data.mods.map((x) => plugEl(x, 'mod'))}</div>}
      {groups.length > 0 && data.columns.length > 0 && !asList && (
        <button type="button" className={`im-wishbtn ${wish ? 'on' : ''}`} onClick={() => setWish(!wish)} aria-pressed={wish}>
          👍 {wish ? 'Hide wish list perks' : 'Preview Wishlist Perks'}
        </button>
      )}
      {tip && <PerkTip plug={tip.plug} rect={tip.rect} modes={likedOf(tip.plug.name)} />}
    </div>
  );
}

/** A stat as DIM draws it: the base white, then what perks, mods and the masterwork add, and in red what was taken away. */
function StatRow({ s }: { s: RolledStat }) {
  const pct = (n: number) => `${Math.max(0, Math.min(100, n))}%`;
  const perks = s.perks ?? 0, mods = s.mods ?? 0, mw = s.mw ?? 0;
  const base = Math.max(0, s.value - perks - mods - mw);
  const tip = [`${s.name}: ${s.value}`, base !== s.value && `base ${base}`, perks && `+${perks} from perks`, mods && `+${mods} from mods`, mw && `+${mw} from the masterwork`, s.minus && `−${s.minus} taken away`].filter(Boolean).join('\n');
  return (
    <div className={`im-stat ${s.masterworked ? 'mw' : ''}`} title={tip}>
      <span>{s.name === 'Airborne Effectiveness' ? 'Airborne' : s.name === 'Ammo Generation' ? 'Ammo Gen' : s.name === 'Rounds Per Minute' ? 'RPM' : s.name}</span>
      <b>{s.value}</b>
      {s.name === 'Recoil Direction' ? <Recoil value={s.value} /> : s.bar ? (
        <i className="bar">
          <i className="b" style={{ width: pct(base) }} />
          {perks > 0 && <i className="p" style={{ width: pct(perks) }} />}
          {mods > 0 && <i className="m" style={{ width: pct(mods) }} />}
          {mw > 0 && <i className="w" style={{ width: pct(mw) }} />}
          {(s.minus ?? 0) > 0 && <i className="n" style={{ width: pct(Math.min(s.minus!, 100 - s.value)) }} />}
        </i>
      ) : <i className="none" />}
    </div>
  );
}

/** Recoil direction as DIM draws it: a wedge pointing where the gun kicks (100 straight up), wider when it kicks more. */
function Recoil({ value }: { value: number }) {
  const v = Math.max(0, Math.min(100, value));
  const angle = Math.sin((v + 5) * ((2 * Math.PI) / 20)) * (100 - v) * 0.8 * (Math.PI / 180);
  const spread = ((100 - v) / 100) * 0.75 * (Math.PI / 2) + 0.06;
  const pt = (a: number) => `${10 + Math.sin(a) * 9},${10 - Math.cos(a) * 9}`;
  return (
    <svg className="im-recoil" viewBox="0 0 20 11" width="30" height="16" aria-label={`Recoil direction ${value}`}>
      <path d="M1 10a9 9 0 0 1 18 0z" fill="rgba(255,255,255,0.18)" />
      <path d={`M10 10L${pt(angle - spread)}A9 9 0 0 1 ${pt(angle + spread)}z`} fill="#fff" />
    </svg>
  );
}

/** The tooltip a perk shows on hover (or tap), in DIM's shape. */
function PerkTip({ plug, rect, modes }: { plug: RolledPlug; rect: DOMRect; modes?: Set<string> }) {
  const w = 300;
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - w / 2, window.innerWidth - w - 8));
  const below = rect.top < 260;
  const style = below ? { left, top: rect.bottom + 8 } : { left, bottom: window.innerHeight - rect.top + 8 };
  return createPortal(
    <div className="im-tip" style={{ ...style, width: w }} role="tooltip">
      <div className="im-tip-head">
        <b>{plug.name}</b>
        <small className={plug.enhanced ? 'enh' : ''}>{plug.enhanced && '▲ '}{plug.type}</small>
      </div>
      {(plug.desc || (plug.stats?.length ?? 0) > 0) && (
        <div className="im-tip-body">
          {plug.desc && <p>{plug.desc}</p>}
          {plug.stats?.map((s) => <p key={s.name} className="im-tip-stat"><b className={s.value < 0 ? 'neg' : ''}>{s.value > 0 ? '+' : ''}{s.value}</b> {s.name}</p>)}
        </div>
      )}
      {modes && <div className="im-tip-wish">👍 In the community's {[...modes].map((m) => (m === 'any' ? 'all-round' : m.toUpperCase())).join(' and ')} god rolls</div>}
    </div>, document.body);
}

/** "Add notes" as DIM has it: a link that opens a box; the note shows in its place once written. */
function NoteRow({ value, save }: { value: string; save: (v: string) => void }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(value);
  if (!edit) return (
    <button type="button" className={`im-card-noterow ${value ? 'has' : ''}`} onClick={() => { setText(value); setEdit(true); }} title="Notes can be searched with notes:">
      <PencilIcon />{value || 'Add notes'}
    </button>
  );
  return (
    <div className="im-card-note">
      <input className="g-input" autoFocus maxLength={120} placeholder="A note (search notes:...)" value={text} onChange={(e) => setText(e.target.value)}
        onBlur={() => { if (text !== value) save(text); setEdit(false); }} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setText(value); setEdit(false); e.stopPropagation(); } }} aria-label="Note" />
    </div>
  );
}

// ---------------- small drawings ----------------

const Ammo = ({ kind }: { kind: number }): ReactNode => (
  <span className={`im-ammo a${kind}`} title={['', 'Primary ammo', 'Special ammo', 'Heavy ammo'][kind]}>
    <svg viewBox="0 0 18 10" width="18" height="10"><path d="M1 8.5V3.5l1.5-2 1.5 2v5zM7 8.5V3.5l1.5-2 1.5 2v5zM13 8.5V3.5l1.5-2 1.5 2v5z" fill="currentColor" opacity={kind === 1 ? 0.35 : 1} /></svg>
  </span>
);
const LockIcon = ({ locked }: { locked: boolean }) => <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d={locked ? 'M4.5 7V5a3.5 3.5 0 0 1 7 0v2' : 'M4.5 7V5a3.5 3.5 0 0 1 6.8-1.2'} /><rect x="3" y="7" width="10" height="7.5" rx="1" fill="currentColor" stroke="none" /></svg>;
const CompareIcon = () => <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M8 2v12M3 4h10M3 4l-2 5h4zM13 4l-2 5h4zM5 14h6" /></svg>;
const PlusIcon = () => <svg viewBox="0 0 16 16" width="16" height="16"><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="M8 4.5v7M4.5 8h7" stroke="#0b2432" strokeWidth="1.8" /></svg>;
const InfuseIcon = () => <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 12l6-8 6 8M5 12l3-4 3 4" /><path d="M2 14h12" /></svg>;
const PencilIcon = () => <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor"><path d="M11.5 1.5l3 3-8.5 8.5H3v-3zM2 14.5h12v1H2z" /></svg>;
const SkullIcon = () => <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor"><path d="M8 1.5a5.5 5.5 0 0 0-3 10.1V14h1.5v-1.5h1V14h1v-1.5h1V14H11v-2.4A5.5 5.5 0 0 0 8 1.5zM5.8 6.2a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6zm4.4 0a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6z" /></svg>;
const CatalystIcon = () => <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor"><path d="M3 2h4v3H3zM9 2h4v3H9zM6.5 6h3v8h-3zM2 6h3v2H2zM11 6h3v2h-3z" /></svg>;
